<template>
  <div class="relative overflow-hidden bg-white rounded-xl shadow-lg border border-gray-200 p-4 mb-6">
    <!-- Fondo decorativo sutil -->
    <div class="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-white to-indigo-50/20"></div>
    <div class="absolute -top-2 -right-2 w-16 h-16 bg-gradient-radial from-blue-100/30 to-transparent rounded-full blur-xl"></div>
    
    <div class="relative z-10">
      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-8 lg:gap-4">
        <!-- Header compacto -->
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
            <!-- Icono dinámico -->
            <component :is="iconComponent" class="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 class="text-xl font-bold bg-gradient-to-r from-slate-800 via-blue-900 to-slate-800 bg-clip-text text-transparent">
              {{ title }}
            </h1>
            <p class="text-slate-500 text-sm">{{ subtitle }}</p>
          </div>
        </div>
        
        <!-- Métricas Compactas -->
        <div class="grid grid-cols-2 lg:flex lg:gap-4 gap-2">
          <!-- Prioridad Alta Compacto -->
          <button @click="$emit('filter-priority', 'alta')" 
                  class="group relative bg-gradient-to-r from-red-50 to-orange-50 hover:from-red-100 hover:to-orange-100 rounded-lg px-3 py-2 transition-all duration-300 hover:scale-105 border border-red-200 hover:border-red-300 shadow-sm hover:shadow-md"
                  :title="`${getTicketsByPriority('alta')} tickets de prioridad alta - Clic para filtrar`">
            <div class="flex items-center space-x-2">
              <div class="w-6 h-6 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-sm group-hover:rotate-12 transition-transform duration-300">
                <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <div class="text-left">
                <p class="text-lg font-bold text-slate-900 leading-none">{{ getTicketsByPriority('alta') }}</p>
                <p class="text-xs text-red-600 font-medium">Alta</p>
              </div>
            </div>
            <div class="absolute top-1 right-1 w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse opacity-60 group-hover:opacity-100"></div>
          </button>
          
          <!-- Tickets Pendientes Compacto -->
          <button @click="$emit('filter-status', 'abierto')" 
                  class="group relative bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 rounded-lg px-3 py-2 transition-all duration-300 hover:scale-105 border border-orange-200 hover:border-orange-300 shadow-sm hover:shadow-md"
                  :title="`${ticketStats.abierto} tickets pendientes - Clic para filtrar`">
            <div class="flex items-center space-x-2">
              <div class="w-6 h-6 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-sm group-hover:rotate-12 transition-transform duration-300">
                <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div class="text-left">
                <p class="text-lg font-bold text-slate-900 leading-none">{{ ticketStats.abierto }}</p>
                <p class="text-xs text-orange-600 font-medium">Pendientes</p>
              </div>
            </div>
            <div class="absolute top-1 right-1 w-1.5 h-1.5 bg-orange-400 rounded-full animate-bounce opacity-60 group-hover:opacity-100"></div>
          </button>
          
          <!-- Tickets En Progreso Compacto -->
          <button @click="$emit('filter-status', 'en_progreso')" 
                  class="group relative bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-lg px-3 py-2 transition-all duration-300 hover:scale-105 border border-blue-200 hover:border-blue-300 shadow-sm hover:shadow-md"
                  :title="`${ticketStats.en_progreso} tickets en progreso - Clic para filtrar`">
            <div class="flex items-center space-x-2">
              <div class="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-sm group-hover:rotate-12 transition-transform duration-300">
                <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <div class="text-left">
                <p class="text-lg font-bold text-slate-900 leading-none">{{ ticketStats.en_progreso }}</p>
                <p class="text-xs text-blue-600 font-medium">En Progreso</p>
              </div>
            </div>
            <div class="absolute top-1 right-1 w-1.5 h-1.5 bg-blue-400 rounded-full animate-spin opacity-60 group-hover:opacity-100"></div>
          </button>
          
          <!-- Tickets Cerrados Compacto -->
          <button @click="$emit('filter-status', 'cerrado')" 
                  class="group relative bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 rounded-lg px-3 py-2 transition-all duration-300 hover:scale-105 border border-green-200 hover:border-green-300 shadow-sm hover:shadow-md"
                  :title="`${ticketStats.cerrado} tickets cerrados - Clic para filtrar`">
            <div class="flex items-center space-x-2">
              <div class="w-6 h-6 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-sm group-hover:rotate-12 transition-transform duration-300">
                <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <div class="text-left">
                <p class="text-lg font-bold text-slate-900 leading-none">{{ ticketStats.cerrado }}</p>
                <p class="text-xs text-green-600 font-medium">Cerrados</p>
              </div>
            </div>
            <div class="absolute top-1 right-1 w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse opacity-60 group-hover:opacity-100"></div>
          </button>
          
          <!-- Total de Tickets Compacto -->
          <div class="group relative bg-gradient-to-r from-slate-50 to-gray-50 hover:from-slate-100 hover:to-gray-100 rounded-lg px-3 py-2 transition-all duration-300 border border-slate-200 shadow-sm"
               :title="`${ticketStats.total} tickets en total`">
            <div class="flex items-center space-x-2">
              <div class="w-6 h-6 rounded-lg bg-gradient-to-br from-slate-600 to-gray-600 flex items-center justify-center shadow-sm">
                <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14-7H5m14 14H5"/>
                </svg>
              </div>
              <div class="text-left">
                <p class="text-lg font-bold text-slate-900 leading-none">{{ ticketStats.total }}</p>
                <p class="text-xs text-slate-600 font-medium">Total</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, h } from 'vue';
import { getGlobalTicketsStore } from '../stores/useTicketsStore';

// Props
const props = defineProps({
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    required: true,
    validator: (value) => ['dashboard', 'tickets'].includes(value)
  }
});

// Events
const emit = defineEmits(['filter-priority', 'filter-status']);

// Store
const ticketsStore = getGlobalTicketsStore();

// Computed properties para iconos
const iconComponent = computed(() => {
  const icons = {
    dashboard: () => h('svg', {
      class: 'w-7 h-7 text-white',
      fill: 'none',
      viewBox: '0 0 24 24',
      stroke: 'currentColor'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M13 10V3L4 14h7v7l9-11h-7z'
      })
    ]),
    tickets: () => h('svg', {
      class: 'w-7 h-7 text-white',
      fill: 'none',
      viewBox: '0 0 24 24',
      stroke: 'currentColor'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z'
      })
    ])
  };
  
  return icons[props.icon] || icons.dashboard;
});

// Computed properties para estadísticas usando el store
const ticketStats = computed(() => {
  // Verificar que el store esté inicializado
  if (!ticketsStore || !ticketsStore.getAllTickets) {
    return {
      total: 0,
      abierto: 0,
      en_progreso: 0,
      resuelto: 0,
      cerrado: 0
    };
  }
  
  // Acceder a getAllTickets, que ahora es un computed directo
  const allTickets = ticketsStore.getAllTickets?.value || [];
  
  // Contar manualmente por estados para asegurar precisión
  const stats = {
    total: allTickets.length,
    abierto: 0,
    en_progreso: 0,
    resuelto: 0,
    cerrado: 0
  };
  
  allTickets.forEach(ticket => {
    const status = ticket.status ? ticket.status.toLowerCase() : '';
    
    switch (status) {
      case 'open':
      case 'abierto':
      case 'pending':
        stats.abierto++;
        break;
      case 'in_progress':
      case 'en_progreso':
      case 'in progress':
        stats.en_progreso++;
        break;
      case 'resolved':
      case 'resuelto':
      case 'complete':
      case 'done':
        stats.resuelto++;
        break;
      case 'closed':
      case 'cerrado':
        stats.cerrado++;
        break;
      default:
        // Por defecto, contar como abierto si no reconocemos el estado
        stats.abierto++;
        break;
    }
  });
  
  return stats;
});

// Funciones auxiliares para contar tickets por criterios específicos
function getTicketsByPriority(priority) {
  const allTickets = ticketsStore?.getAllTickets?.value || [];
  return allTickets.filter(ticket => {
    const ticketPriority = ticket.priority ? ticket.priority.toLowerCase() : '';
    return ticketPriority === priority.toLowerCase();
  }).length;
}
</script>
